import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { 
  Plus, Search, Edit, Trash2, X, Loader, AlertTriangle, 
  MessageCircle, Star, Save, CheckCircle2, User
} from 'lucide-react';
import { 
  fetchAdminTestimonials, 
  createAdminTestimonial, 
  updateAdminTestimonial, 
  deleteAdminTestimonial 
} from '../Services/userServices';

const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '', role: '', review: '', rating: 5,
    is_active: true, display_order: 0,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const loadItems = async () => {
    setIsLoading(true);
    setError('');
    const result = await fetchAdminTestimonials({ 
      search: searchQuery, 
      is_active: statusFilter 
    });
    if (result.success) setItems(result.data || []);
    else setError(result.error);
    setIsLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => loadItems(), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter]);

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name || '',
        role: item.role || '',
        review: item.review || '',
        rating: item.rating || 5,
        is_active: item.is_active,
        display_order: item.display_order || 0,
      });
      setImagePreview(item.image || '');
    } else {
      setEditingItem(null);
      setFormData({ name: '', role: '', review: '', rating: 5, is_active: true, display_order: 0 });
      setImagePreview('');
    }
    setImageFile(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setImageFile(null);
    setImagePreview('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.review.trim().length < 10) {
      alert('Review कम से कम 10 अक्षरों का होना चाहिए।');
      return;
    }
    setIsSubmitting(true);
    const result = editingItem
      ? await updateAdminTestimonial(editingItem.id, formData, imageFile)
      : await createAdminTestimonial(formData, imageFile);

    if (result.success) {
      setSuccessMessage(result.message);
      closeModal();
      loadItems();
      setTimeout(() => setSuccessMessage(''), 3000);
    } else {
      alert(result.error);
    }
    setIsSubmitting(false);
  };

  const executeDelete = async () => {
    if (!deleteId) return;
    const result = await deleteAdminTestimonial(deleteId);
    if (result.success) {
      setItems((prev) => prev.filter((e) => e.id !== deleteId));
      setSuccessMessage(result.message);
      setTimeout(() => setSuccessMessage(''), 3000);
    } else {
      alert(result.error);
    }
    setDeleteId(null);
  };

  return (
    <Layout>
      <div className="space-y-6 pb-12">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">Client Testimonials</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Manage verified customer endorsements shown on homepage</p>
          </div>
          <button 
            onClick={() => openModal()}
            className="px-5 py-2.5 bg-[#10B981] hover:bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all text-xs flex items-center gap-2 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Add Testimonial</span>
          </button>
        </div>

        {successMessage && (
          <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-bold text-sm flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5" />
            <span>{successMessage}</span>
          </div>
        )}

        {error && (
          <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-bold text-sm flex items-center gap-3">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {/* SEARCH & FILTERS */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, role, or review content..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-xs font-medium"
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto">
            {['All', 'Active', 'Inactive'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                  statusFilter === status
                    ? 'bg-slate-900 text-white dark:bg-emerald-500 shadow-sm'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700/60'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* GRID */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader className="w-8 h-8 text-[#10B981] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center bg-white dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800">
            <MessageCircle className="w-10 h-10 text-slate-400 mx-auto mb-3 opacity-60" />
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">No Testimonials Yet</h3>
            <p className="text-xs text-slate-500 mt-1">Add your first customer endorsement to boost credibility</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((t) => (
              <div key={t.id} className="bg-white dark:bg-slate-900/50 rounded-2xl shadow-sm dark:shadow-xl border border-slate-200 dark:border-slate-800 p-6 hover:shadow-md hover:border-emerald-500/30 transition-all">
                <div className="flex items-start gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
                  {t.image ? (
                    <img src={t.image} alt={t.name} className="w-12 h-12 rounded-full object-cover border-2 border-emerald-500/30" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-600 font-black">
                      {t.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">{t.name}</h3>
                    <p className="text-[10px] font-extrabold text-[#10B981] uppercase tracking-wider mt-0.5 truncate">{t.role}</p>
                    <div className="flex gap-0.5 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3 h-3 ${i < t.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                      ))}
                    </div>
                  </div>
                </div>

                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed mt-3 italic line-clamp-3">
                  "{t.review}"
                </p>

                <div className="mt-4 flex items-center justify-between text-[10px]">
                  <span className={`px-2 py-0.5 font-extrabold uppercase tracking-widest rounded-full ${
                    t.is_active 
                      ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
                      : 'bg-slate-500/10 text-slate-500 border border-slate-500/20'
                  }`}>
                    {t.is_active ? 'Live on Homepage' : 'Hidden'}
                  </span>
                  <span className="text-slate-400 font-bold">Order: #{t.display_order}</span>
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800 flex gap-2">
                  <button 
                    onClick={() => openModal(t)}
                    className="flex-1 px-3 py-2 text-xs font-bold bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 rounded-xl border border-emerald-500/20 flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Edit className="w-3 h-3" /> Edit
                  </button>
                  <button 
                    onClick={() => setDeleteId(t.id)}
                    className="flex-1 px-3 py-2 text-xs font-bold bg-red-500/10 hover:bg-red-500/20 text-red-600 rounded-xl border border-red-500/20 flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ADD/EDIT MODAL */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleSubmit}>
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800/80">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-[#10B981]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                        {editingItem ? 'Edit Testimonial' : 'Add New Testimonial'}
                      </h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        Manage customer endorsement for homepage
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={closeModal} className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 cursor-pointer">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  {/* Photo Upload */}
                  <div className="flex items-center gap-4">
                    <label className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 border-2 border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center cursor-pointer overflow-hidden hover:border-[#10B981]">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-6 h-6 text-slate-400" />
                      )}
                      <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                    </label>
                    <div>
                      <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Customer Photo</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Click to upload (Optional)</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Customer Name *</label>
                      <input
                        type="text" required value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g. Rahul Sharma"
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Role / Company *</label>
                      <input
                        type="text" required value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        placeholder="e.g. Estate Owner"
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Review *</label>
                    <textarea
                      required rows="4" value={formData.review}
                      onChange={(e) => setFormData({ ...formData, review: e.target.value })}
                      placeholder="Customer's detailed feedback about your services..."
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Rating (1-5)</label>
                      <select
                        value={formData.rating}
                        onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium cursor-pointer"
                      >
                        {[5, 4, 3, 2, 1].map((r) => (
                          <option key={r} value={r}>{'★'.repeat(r)} ({r})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Order</label>
                      <input
                        type="number" min="0" value={formData.display_order}
                        onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Status</label>
                      <select
                        value={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-sm font-medium cursor-pointer"
                      >
                        <option value="true">Live (Show)</option>
                        <option value="false">Hidden</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-slate-200 dark:border-slate-800/80 flex items-center justify-end gap-3">
                  <button type="button" onClick={closeModal} className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold text-xs cursor-pointer">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className="px-5 py-2.5 rounded-xl bg-[#10B981] hover:bg-emerald-600 text-white font-bold text-xs shadow-md flex items-center gap-2 disabled:opacity-50 cursor-pointer">
                    {isSubmitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    <span>{editingItem ? 'Save Changes' : 'Add Testimonial'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* DELETE CONFIRMATION */}
        {deleteId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl max-w-sm w-full shadow-2xl text-center space-y-5">
              <div className="w-12 h-12 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto border border-red-500/20">
                <Trash2 className="w-5 h-5 animate-pulse" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Delete Testimonial?</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  This will permanently remove this endorsement from your records and homepage.
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-bold cursor-pointer">
                  Cancel
                </button>
                <button onClick={executeDelete} className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold shadow-md cursor-pointer">
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Testimonials;